/**
 * Lykke on the home: one central input (text, voice, files: the same composer as everywhere), under the welcome row of
 * the existing overview. Sending moves the conversation to Lykke's own page (/lykke), where it continues. Plus the
 * home's "Needs you" block (decisions and answers, with status updates under it) for the overview's needs card.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { lykkeApi } from './lykkeApi';
import { HOME, useLykke } from './LykkeContext';
import { LykkeComposer } from './LykkeComposer';
import { LykkeAvatar } from './LykkeAvatar';
import { NeedsYouList, useNeedsYou, useRunNeed } from './NeedsYou';
import { useShell } from '@/cowork/shell/ShellContext';

export function LykkeHomeBar() {
  const l = useLykke();
  const navigate = useNavigate();
  // New layout: Lykke's panel is on every page, so the conversation continues there (no page change).
  const shell = useShell();
  const threads = useQuery({ queryKey: ['lykke', 'threads'], queryFn: () => lykkeApi.threads(), enabled: !!l?.available, staleTime: 30_000, retry: 1 });
  if (!l?.available) return null;
  const last = (threads.data?.threads || []).find((t) => t.lastText);
  return (
    <section aria-label="Ask Lykke" className="rounded-[24px] bg-white p-5 shadow-sm md:p-6" data-lykke-home-bar>
      <div className="mb-3 flex items-center gap-3">
        <LykkeAvatar size={36} live={l.voice !== 'off'} />
        <div className="min-w-0 flex-1 leading-tight">
          <h2 className="text-[17px] font-semibold text-[#101828]" style={{ fontFamily: 'var(--font-display)' }}>Ask Lykke anything about your campaigns, content or brand</h2>
          <div className="text-[12.5px] text-[#667085]">Your agent at Beatly. She answers from your data and takes you where you need to go.</div>
        </div>
        {last && (
          <button type="button" onClick={() => (shell && !last.campaignId ? shell.focusAgent() : navigate(last.campaignId ? `/lykke/${last.campaignId}` : '/lykke'))} className="hidden shrink-0 items-center gap-1 text-[12.5px] font-semibold text-[#7a1fbf] hover:underline sm:inline-flex">
            Continue the conversation<ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <LykkeComposer scope={HOME} size="large" placeholder="Ask about a campaign, a creator's draft, your results…"
        onSend={(text, files, raw) => {
          if (shell) {
            shell.setView('lykke'); shell.focusAgent();
            void (async () => { if (!l.loaded(HOME)) await l.load(HOME); await l.send(HOME, text, undefined, files, raw); })();
            return;
          }
          navigate('/lykke');
          // The earlier conversation loads first, so the new message lands under it on the Lykke page.
          void (async () => { if (!l.loaded(HOME)) await l.load(HOME); await l.send(HOME, text, undefined, files); })();
        }} />
    </section>
  );
}

/** The overview's "Needs you": decisions and answers, one button each; status news (launched, results ready) under it. */
export function HomeNeedsYou() {
  const l = useLykke();
  const needs = useNeedsYou(!!l?.available);
  const run = useRunNeed();
  const todo = (needs.data || []).filter((n) => n.kind !== 'status');
  const updates = (needs.data || []).filter((n) => n.kind === 'status');
  if (needs.isError) return <p className="text-sm text-[#667085]">Could not load this. It comes back on its own.</p>;
  return (
    <div>
      <NeedsYouList items={todo} loading={needs.isLoading} />
      {updates.length > 0 && (
        <div className="mt-4 border-t border-[#f2f4f7] pt-3" data-lykke-updates>
          <div className="mb-1 text-[12px] font-semibold uppercase tracking-[0.04em] text-[#98a2b3]">Updates</div>
          <ul className="space-y-1">
            {updates.map((u) => (
              <li key={u.key}>
                <button type="button" onClick={() => run(u)} className="flex w-full items-center gap-2 rounded-lg px-1 py-1 text-left text-[13px] text-[#475467] hover:bg-[#f9fafb]">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#a9f600]" /><span className="truncate"><span className="font-semibold text-[#101828]">{u.campaignName}</span>: {u.title.toLowerCase()}{u.detail ? `. ${u.detail}` : ''}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
